import React, { useContext, useEffect, useMemo } from "react";
import { Outlet, Navigate, Route, Routes } from "react-router-dom";
import PropTypes from "prop-types";
import Loader from "../Loader";

import Context from "../../context";
import { hasRoles } from "../../utils/security";
import useUser from "../../hooks/useUser";

const useAuth = context => {
  useEffect(() => {
    if (!context?.keycloak.authenticated) {
      context.login();
    }
  }, [context]);
};

const PrivateRoute = ({ roles, accessDeniedUrl, accessDeniedComponent, loader }) => {
  const context = useContext(Context);
  useAuth(context);
  const user = useUser();
  const isAuthorized = useMemo(() => user && hasRoles(roles, user.roles), [roles, user]);

  if (!user) {
    return (
      <Routes>
        <Route path="*" element={loader} />
      </Routes>
    );
  }

  if (!isAuthorized) {
    return accessDeniedComponent ? (
      <Routes>
        <Route path="*" element={accessDeniedComponent} />
      </Routes>
    ) : (
      <Navigate to={accessDeniedUrl} />
    );
  }

  return <Outlet />;
};

PrivateRoute.displayName = "PrivateRoute";

PrivateRoute.defaultProps = {
  accessDeniedUrl: "/access-denied",
  accessDeniedComponent: null,
  loader: <Loader />,
  roles: null
};

PrivateRoute.propTypes = {
  /** React element that can be rendered when user in authentication process */
  loader: PropTypes.element,
  /** React element that can be rendered  when access is denied */
  accessDeniedComponent: PropTypes.node,
  /** Url to redirect when user dont Authorized */
  accessDeniedUrl: PropTypes.string,
  /** Object with properties. The property of object is keycloak client name, the value is array with keycloak roles.
   *  Example: { firstClient: [ "ADMIN" ], secondClient: [ "MANAGER" ] }
   */
  roles: PropTypes.object
};

export default PrivateRoute;
